import React, { useState, useEffect } from "react";
import {
  Search, Clock, ChevronDown, ChevronRight, RotateCcw, MessageSquare, AlertTriangle,
  Building2, Package, Factory, Truck, Link2, ShoppingBag, Globe, Filter, X
} from "lucide-react";
import ModalPop from "./ModalPop";
import AddModal from "./AddModal";
import { Shield } from 'lucide-react';
import heartLoading from "../../../assests/heartLoading.gif";
import axios from "axios";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';



const BASE_URL = process.env.REACT_APP_API_URL;

const GlossaryDiscovery = ({
  glossaryTerms = [],
  glossaryLoading = false,
  currentPage = 1,
  totalCount = 0,
  pageSize = 20,
  onPageChange,
  onSearch,
  onOpenFilters,
  onSortChange,
  onFeedbackSubmit,
  onHierarchyFilter,
  hierarchyData = [],
  hierarchyLoading = false,
  syncStatus = null,
  syncLoading = true,
}) => {
  const totalPages = Math.ceil(totalCount / pageSize);
  const [viewMode, setViewMode] = useState("list");
  const [expandedItems, setExpandedItems] = useState({ "l1": true });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [assetDetail, setAssetDetail] = useState(null);
  const [assetDetailLoading, setAssetDetailLoading] = useState(false);
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [sidebarWidth, setSidebarWidth] = useState(270);
  const [selectedHierarchy, setSelectedHierarchy] = useState({
    parentDomain: [],
    domain: [],
    subDomain: [],
  });
  const [isCollapsed, setIsCollapsed] = useState(false);

  const transformedHierarchyData = React.useMemo(() => {
    return hierarchyData.map((parentDomain) => ({
      id: parentDomain.parent_domain_id ?? parentDomain.parent_domain,
      name: parentDomain.parent_domain,
      icon: Building2,
      children: parentDomain.domains.map((domain) => ({
        id: domain.domain_id,
        name: domain.domain,
        icon: Package,
        children: domain.sub_domains.map((subDomain) => ({
          id: subDomain.sub_domain_id,
          name: subDomain.sub_domain,
          icon: Factory,
        })),
      })),
    }));
  }, [hierarchyData]);




  const formatSyncDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    const day = String(d.getUTCDate()).padStart(2, "0");
    const month = d.toLocaleString("en-GB", { month: "short", timeZone: "UTC" });
    const year = String(d.getUTCFullYear()).slice(-2);
    const hours = String(d.getUTCHours()).padStart(2, "0");
    const mins = String(d.getUTCMinutes()).padStart(2, "0");
    return `${day} ${month} '${year}, ${hours}:${mins}`;
  };

  const stripHtml = (html) => {
    if (!html) return "";
    // Check if it contains HTML tags
    if (/<[^>]+>/.test(html)) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      return doc.body.textContent?.trim() || "";
    }
    return html;
  }

  const handleHierarchyCheck = (item, level, checked, parentItem = null) => {
    setSelectedHierarchy((prev) => {
      const next = {
        parentDomain: [...prev.parentDomain],
        domain: [...prev.domain],
        subDomain: [...prev.subDomain],
      };
      if (level === 0) {
        const domainNames = getAllDomainNames(item);
        const subDomainNames = getAllSubDomainNamesUnderParent(item);
        if (checked) {
          next.parentDomain = [...new Set([...next.parentDomain, item.name])];
          next.domain = [...new Set([...next.domain, ...domainNames])];
          next.subDomain = [...new Set([...next.subDomain, ...subDomainNames])];
        } else {
          next.parentDomain = next.parentDomain.filter((n) => n !== item.name);
          next.domain = next.domain.filter((n) => !domainNames.includes(n));
          next.subDomain = next.subDomain.filter((n) => !subDomainNames.includes(n));
        }
      } else if (level === 1) {
        const subDomainNames = getAllSubDomainNamesUnderDomain(item);
        if (checked) {
          next.domain = [...new Set([...next.domain, item.name])];
          next.subDomain = [...new Set([...next.subDomain, ...subDomainNames])];
        } else {
          next.domain = next.domain.filter((n) => n !== item.name);
          next.subDomain = next.subDomain.filter((n) => !subDomainNames.includes(n));
          if (parentItem) next.parentDomain = next.parentDomain.filter((n) => n !== parentItem.name);
        }
      } else {
        if (checked) {
          next.subDomain = [...new Set([...next.subDomain, item.name])];
        } else {
          next.subDomain = next.subDomain.filter((n) => n !== item.name);
          if (parentItem) next.domain = next.domain.filter((n) => n !== parentItem.name);
        }
      }
      return next;
    });
  };

  const handleResetHierarchy = () => {
    setSelectedHierarchy({ parentDomain: [], domain: [], subDomain: [] });
  };


  const handleMouseDown = (e) => {
    if (isCollapsed) return;
    e.preventDefault();
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const startX = e.clientX;
    const startWidth = sidebarWidth;

    const onMouseMove = (e) => {
      const newWidth = Math.min(280, Math.max(150, startWidth + (e.clientX - startX)));
      setSidebarWidth(newWidth);
    };

    const onMouseUp = () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const fetchAssetDetail = async (id) => {
    try {
      setAssetDetailLoading(true);
      const res = await axios.get(`${BASE_URL}/collibra/asset/${id}`);
      setAssetDetail(res.data);
    } catch (e) {
      console.error("Failed to fetch asset detail", e);
      setAssetDetail(null);
    } finally {
      setAssetDetailLoading(false);
    }
  };

  useEffect(() => {
    onSortChange?.(sortBy, sortOrder);
  }, [sortBy, sortOrder]);
  useEffect(() => {
    onHierarchyFilter?.(selectedHierarchy);
  }, [selectedHierarchy]);

  useEffect(() => {
    const timer = setTimeout(() => {
      onSearch?.(searchTerm);
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const levelColors = {
    0: { text: "#DC2626", bg: "#FEE2E2", border: "#FECACA" },  // L1 - Red
    1: { text: "#16A34A", bg: "#DCFCE7", border: "#BBF7D0" },  // L2 - Green
    2: { text: "#D97706", bg: "#FEF3C7", border: "#FDE68A" },  // L3 - Amber
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleAddTerm = (newTermData) => {
    const newTerm = {
      id: glossaryTerms.length + 1,
      term: newTermData.term,
      status: newTermData.status,
      definition: newTermData.definition,
      owner: newTermData.owner,
      classification: newTermData.classification,
      date: new Date().toISOString().split('T')[0],
      fullDefinition: newTermData.definition,
      formula: newTermData.formula,
      department: newTermData.department,
      certificate: newTermData.certificate,
      businessContext: newTermData.businessContext,
      dataType: newTermData.dataType,
      source: newTermData.source,
      tags: newTermData.tags
    };
  };
  const getAllDomainNames = (parentItem) =>
    (parentItem.children || []).map((d) => d.name);

  const getAllSubDomainNamesUnderParent = (parentItem) =>
    (parentItem.children || []).flatMap((d) =>
      (d.children || []).map((s) => s.name)
    );

  const getAllSubDomainNamesUnderDomain = (domainItem) =>
    (domainItem.children || []).map((s) => s.name);

  const renderHierarchyItem = (item, level = 0, parentItem = null) => {
    const isExpanded = expandedItems[item.id];
    const hasChildren = item.children && item.children.length > 0;
    const Icon = item.icon;
    const key = level === 0 ? "parentDomain" : level === 1 ? "domain" : "subDomain";
    const isChecked = selectedHierarchy[key].includes(item.name);

    return (
      <div key={item.id}>
        <div
          className={`flex items-center px-2 py-1.5 text-sm rounded-sm mx-1 ${hasChildren ? "cursor-pointer hover:bg-gray-100" : "cursor-default hover:bg-gray-50"
            } ${isChecked ? "bg-gray-50" : ""}`}
          style={{ marginLeft: `${4 + level * 14}px` }}
          onClick={() => hasChildren && toggleExpand(item.id)}
        >
          {hasChildren ? (
            isExpanded
              ? <ChevronDown size={13} className="mr-1 text-gray-400 flex-shrink-0" />
              : <ChevronRight size={13} className="mr-1 text-gray-400 flex-shrink-0" />
          ) : (
            <span className="w-[18px] inline-block flex-shrink-0" />
          )}

          <input
            type="checkbox"
            className="mr-1.5 flex-shrink-0 accent-indigo-600 cursor-pointer"
            checked={isChecked}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleHierarchyCheck(item, level, e.target.checked, parentItem)}
          />

          {Icon && (
            <Icon size={13} className="mr-1.5 flex-shrink-0"
              style={{ color: levelColors[level]?.text }}
            />
          )}

          <span
            className="truncate text-[12px]"
            style={{
              color: isChecked ? levelColors[level]?.text : "#374151",
              fontWeight: isChecked ? 600 : 400
            }}
          >
            {item.name}
          </span>

          {item.count && (
            <span className="ml-auto bg-gray-100 px-1.5 py-0.5 rounded-xl text-[10px] text-gray-500 flex-shrink-0">
              {item.count}
            </span>
          )}
        </div>

        {isExpanded && hasChildren && (
          <div
            style={{
              marginLeft: `${20 + level * 20}px`,
              borderLeft: "1.5px solid #e5e7eb",
              marginTop: "2px",
              marginBottom: "2px",
              paddingLeft: "4px",
            }}
          >
            {item.children.map((child) => renderHierarchyItem(child, level + 1, item))}
          </div>
        )}
      </div>
    );
  };



  return (
    <>
      <div className="flex h-[calc(100vh-200px)] bg-white">
        {/* Left Sidebar */}
        <div
          className="border-r border-gray-200 flex-shrink-0 relative flex flex-col"
          style={{
            width: isCollapsed ? '0px' : `${sidebarWidth}px`,
            overflow: 'visible',
            transition: 'width 0.22s cubic-bezier(.4,0,.2,1)',
            minWidth: isCollapsed ? '0px' : undefined,
          }}
        >
          {/* Inner wrapper - hides content when collapsed */}
          <div style={{
            opacity: isCollapsed ? 0 : 1,
            transition: 'opacity 0.15s',
            overflow: 'hidden',
            width: `${sidebarWidth}px`,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div className="flex items-center justify-between px-1 pt-2 pb-2">
              <span className="text-[10px] font-semibold text-gray-500 tracking-wider uppercase">
                Pulse Hierarchy Levels
              </span>
            </div>
            <button
              onClick={handleResetHierarchy}
              className="flex items-center gap-1 mb-2 text-[10px] w-[60px] font-semibold px-2 py-1 rounded-md border border-gray-300 text-gray-500 hover:bg-red-50 hover:text-red-500 hover:border-red-300 transition-colors"
            >
              <RotateCcw size={10} />
              Reset
            </button>

            <div className="flex gap-1.5 mb-3 flex-wrap px-2">
              <span
                className="inline-flex items-center gap-1 py-[3px] px-2.5 text-[11px] font-normal rounded-[6px] tracking-[0.03em]"
                style={{
                  backgroundColor: levelColors[0].bg,
                  color: levelColors[0].text,
                  border: `1.5px solid ${levelColors[0].border}`,
                }}
              >
                <Building2 size={11} color={levelColors[0].text} /> L1
              </span>

              <span
                className="inline-flex items-center gap-1 py-[3px] px-2.5 text-[11px] font-normal rounded-[6px] tracking-[0.03em]"
                style={{
                  backgroundColor: levelColors[1].bg,
                  color: levelColors[1].text,
                  border: `1.5px solid ${levelColors[1].border}`,
                }}
              >
                <Package size={11} color={levelColors[1].text} /> L2
              </span>

              <span
                className="inline-flex items-center gap-1 py-[3px] px-2.5 text-[11px] font-normal rounded-[6px] tracking-[0.03em]"
                style={{
                  backgroundColor: levelColors[2].bg,
                  color: levelColors[2].text,
                  border: `1.5px solid ${levelColors[2].border}`,
                }}
              >
                <Factory size={11} color={levelColors[2].text} /> L3
              </span>
            </div>



            {/* Tree */}
            <div className="flex-1 overflow-y-auto pb-4">
              {hierarchyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <img src={heartLoading} alt="Loading..." className="h-16 w-16 mx-auto mb-2" />
                    <p className="text-gray-600 text-xs">Loading hierarchy...</p>
                  </div>
                </div>
              ) : transformedHierarchyData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center px-2">
                  <p className="text-sm text-gray-400">No hierarchy data available</p>
                </div>
              ) : (
                transformedHierarchyData.map((item) => renderHierarchyItem(item, 0, null))
              )}
            </div>
          </div>
          {/* Resize handle */}
          <div
            onMouseDown={handleMouseDown}
            onMouseEnter={e => { if (!isCollapsed) e.currentTarget.style.backgroundColor = '#bb1ccc'; }}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            className={`absolute top-0 right-0 h-full w-[4px] transition-colors duration-150 z-10 ${isCollapsed ? 'cursor-default' : 'cursor-col-resize'}`}
          />

          {/* TOGGLE ARROW BUTTON */}
          <button
            onClick={() => setIsCollapsed(prev => !prev)}
            className="absolute top-1/2 -translate-y-1/2 -right-[13px] w-[26px] h-[26px] rounded-full bg-[#bb1ccc] border-2 border-white flex items-center justify-center cursor-pointer z-30 shrink-0"
          >
            <svg
              width="12" height="12" viewBox="0 0 12 12" fill="none"
              className="transition-transform duration-[220ms]"
              style={{ transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              <path d="M7 2L3 6L7 10" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">

          {/* Header */}
          <div className="flex flex-wrap mb-2 justify-between px-3">
            <div className="relative mr-[5px] flex items-center gap-2">
              <div className="relative w-[200px]">
                <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search Terms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-1.5 pl-5 pr-8 border border-gray-300 rounded-md text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      onSearch?.("");
                    }}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-none border-none cursor-pointer"
                    type="button"
                  >
                    <X size={10} />
                  </button>
                )}
              </div>
            </div>
            {/* Sort By */}

            <div className="flex gap-2 items-center">
              {syncLoading ? (

                <div className="flex items-center gap-2 border border-gray-200 rounded-md px-3 py-1.5 bg-gray-50 animate-pulse">
                  <div className="w-2 h-2 rounded-full bg-gray-200 flex-shrink-0" />
                  <div className="h-3 w-16 bg-gray-200 rounded" />
                  <div className="h-3 w-24 bg-gray-200 rounded" />
                  <div className="h-4 w-14 bg-gray-200 rounded-full" />
                </div>
              ) : syncStatus ? (
                <div className="flex items-center gap-2 text-xs text-black border border-gray-200 rounded-md px-3 py-1.5 bg-gray-50">
                  <span
                    className="inline-block !w-2 !h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: syncStatus.status === "SUCCESS" ? "#16a34a" : "#dc2626" }}
                  />
                  <span className="font-medium text-black">Last Sync:</span>
                  <span>{formatSyncDate(syncStatus.completedAt)}</span>
                  <span
                    className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                    style={{
                      backgroundColor: syncStatus.status === "SUCCESS" ? "#dcfce7" : "#fee2e2",
                      color: syncStatus.status === "SUCCESS" ? "#15803d" : "#dc2626",
                    }}
                  >
                    {syncStatus.status}
                  </span>
                </div>
              ) : null}

              <p>Sort by: </p>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-xs  font-semibold border border-gray-300 rounded-md px-2 py-1.5 h-[34px] outline-none focus:border-[#bb1ccc] cursor-pointer"
              >
                <option value="name">Asset Name</option>
                <option value="lastModifiedOn">Last Modified Date</option>
              </select>

              {/* Sort Order */}
              <div className="flex h-[33px] border border-gray-300 rounded-md overflow-hidden">
                <button
                  onClick={() => setSortOrder("asc")}
                  className={`px-3 text-xs font-semibold transition-colors ${sortOrder === "asc" ? "bg-[#bb1ccc] text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  ASC
                </button>
                <button
                  onClick={() => setSortOrder("desc")}
                  className={`px-3 text-xs font-semibold border-l border-gray-300 transition-colors ${sortOrder === "desc" ? "bg-[#bb1ccc] text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  DESC
                </button>
              </div>
              <button onClick={() => onOpenFilters?.()} className="flex items-center gap-2  ml-5 px-3 py-1.5 text-sm font-medium rounded-lg border h-[34px] whitespace-nowrap bg-[#FF03F3] text-white border-[#FF03F3]"  >    <Filter size={14} />    Filters  </button>
            </div>

          </div>

          <div className="px-3 pb-4 pt-1 border-b border-gray-200">
            <div className="flex justify-between items-center">

              <div className="mb-[10px]">
                <span className="bg-[#F0FDFA] text-[#14B8A6] text-sm px-3 py-1 rounded-md shadow-sm">
                There are   {totalCount} Unique Results Found
                </span>
              </div>
              {totalPages >= 1 && (
                <div className="flex items-center gap-1">

                  <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-2 py-2 rounded text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                  >
                    Prev
                  </button>

                  {(() => {
                    const pages = [];
                    if (totalPages <= 5) {
                      for (let i = 1; i <= totalPages; i++) pages.push(i);
                    } else {
                      pages.push(1);
                      if (currentPage > 4) pages.push("...");
                      for (
                        let i = Math.max(2, currentPage - 2);
                        i <= Math.min(totalPages - 1, currentPage + 2);
                        i++
                      ) {
                        pages.push(i);
                      }
                      if (currentPage < totalPages - 3) pages.push("...");
                      pages.push(totalPages);
                    }

                    return pages.map((page, idx) =>
                      page === "..." ? (
                        <span key={`dot-${idx}`} className="px-3 py-2 text-gray-400 text-sm">
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => onPageChange(page)}
                          className="px-3 py-2 rounded text-sm font-medium border"
                          style={{
                            backgroundColor: currentPage === page ? "#FF03F3" : "#fff",
                            color: currentPage === page ? "#fff" : "#374151",
                            borderColor: currentPage === page ? "#FF03F3" : "#D1D5DB",
                          }}
                        >
                          {page}
                        </button>
                      )
                    );
                  })()}

                  <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-2 py-2 rounded text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                  >
                    Next
                  </button>

                </div>
              )}
            </div>
          </div>

          {/* Terms List */}
          <div className="flex-1 overflow-y-auto p-3 bg-[#f2f2f3]">
            {glossaryLoading ? (
              <div className="flex items-center justify-center py-12 mt-[3rem]">
                <div className="flex flex-col items-center  justify-center space-y-3">
                  <img
                    src={heartLoading}
                    alt="Loading..."
                    className="h-28 mx-auto mb-2"
                  />
                  <p className="text-gray-600 text-[18px]">
                    Loading  details...
                  </p>
                </div>
              </div>


            ) : glossaryTerms.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <h3 className="text-base font-semibold text-gray-600 mb-1">No results found</h3>
                <p className="text-sm text-gray-400 max-w-[280px]">
                  No glossary terms match your search or filters. Try adjusting your query.
                </p>
              </div>
            ) : (
              <div className="space-y-3" style={{ minWidth: 'max-content' }}>
                {glossaryTerms.map((term) => (
                  <div
                    key={term.id}
                    className="bg-white rounded-lg border border-gray-200 p-3 cursor-pointer hover:shadow-md h-[90px] transition-shadow flex justify-between items-start" style={{ minWidth: '100%' }}
                    onClick={() => { setSelectedTerm(term); fetchAssetDetail(term.id); }}
                  >
                    {/* Left Side - Term Info */}
                    <div className="flex-shrink-0 pr-4">
                      {/* Term Header */}
                      <div className="flex items-center gap-2 mb-3">
                        <h3 className="text-base font-bold  text-[#5a58ec] truncate">
                          {term.displayName}
                        </h3>

                        <span className="text-[11px] text-[#EDE9FE] bg-[#bb1ccc] px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                          <Tooltip arrow title={term.statusDescription ? stripHtml(term.statusDescription) : ""}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                              <InfoOutlineIcon sx={{ fontSize: '13px !important' }} />
                            </span>
                          </Tooltip>
                          {term.status}
                        </span>
                        {term.cl3Community ? (
                          <span className="text-[11px] text-[#CD0B7F] bg-[#F6EBF2] px-2 py-0.5 rounded-full font-medium">
                            {term.cl3Community}
                          </span>
                        ) : (
                          <span className="text-[11px] text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full font-medium">
                            Community Level Not Mapped
                          </span>
                        )}
                      </div>

                      {/* Definition */}
                      <Tooltip title={stripHtml(term.description) || ""} arrow>
                        <p className={`text-sm leading-relaxed truncate ${!stripHtml(term.description) ? "text-gray-400 italic" : "text-black"}`}>  {stripHtml(term.description)?.length > 50 ? stripHtml(term.description).slice(0, 50) + "..." : stripHtml(term.description) || "No description available"}</p>
                      </Tooltip>
                    </div>

                    {/* Right Side - Metadata */}
                    <div className="flex items-center gap-4 text-xs text-black" style={{ flexShrink: 0, marginTop: '10px' }}>
                      {/* Owner */}
                      <div className="flex items-center gap-2" style={{ width: '150px' }}>
                        <div style={{
                          width: '20px', height: '20px', borderRadius: '50%',
                          backgroundColor: !term.owner || term.owner === "Not Available" ? '#14b8a6' : '#14b8a6',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '9px', fontWeight: 600,
                          color: !term.owner || term.owner === "Not Available" ? '#fff' : '#ffffff',
                          flexShrink: 0
                        }}>
                          {!term.owner || term.owner === "Not Available" ? "NA" : term.owner?.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </div>
                        <span className="text-[13px] truncate" style={{ color: !term.owner || term.owner === "Not Available" ? '#9ca3af' : 'inherit' }}>
                          {!term.owner || term.owner === "Not Available" ? "Not Available" : term.owner}
                        </span>
                      </div>
                      <div className="flex items-center " style={{ width: '170px' }}>

                        {term.isCDE === "yes" ? (
                          <span className="text-[13px] truncate text-green-700 font-medium px-2.5 py-0.5 rounded-full border border-green-300 flex items-center gap-1.5" style={{ backgroundColor: '#ddffd6' }}>
                            <span className="rounded-full bg-green-500 inline-block" style={{ width: '6px', height: '6px' }} />
                            Is CDE : Yes
                          </span>
                        ) : term.isCDE === "no" ? (
                          <span className="text-[13px] truncate font-medium px-2.5 py-0.5 rounded-full border flex items-center gap-1.5" style={{ color: '#D17A00', backgroundColor: '#FFF3E0', borderColor: '#f5c47a' }}>
                            <span className="rounded-full inline-block" style={{ width: '6px', height: '6px', backgroundColor: '#D17A00' }} />
                            Is CDE : No
                          </span>
                        ) : (
                          <span className="text-[13px] truncate text-gray-500 font-medium px-2.5 py-0.5 rounded-full border border-gray-300 flex items-center gap-1.5" style={{ backgroundColor: '#F3F4F6' }}>
                            <span className="rounded-full bg-gray-400 inline-block" style={{ width: '6px', height: '6px' }} />
                            Is CDE : N/A
                          </span>
                        )}
                      </div>
                      {/* Classification */}
                      <div className="flex items-center gap-1.5" style={{ width: '120px' }}>
                        <Shield size={12} className="text-[#FF03F3]" style={{ flexShrink: 0 }} />
                        <span className="text-[13px] truncate">{term.assetType}</span>
                      </div>

                      {/* Date */}
                      <div className="flex items-center gap-1.5" style={{ width: '140px' }}>
                        <Clock size={14} className="text-[#14B8A6]" style={{ flexShrink: 0 }} />
                        <span className="text-[13px] whitespace-nowrap">
                          {term?.date
                            ? (() => {
                              const d = new Date(term.date);
                              const day = String(d.getUTCDate()).padStart(2, "0");
                              const month = d.toLocaleString("en-GB", { month: "short", timeZone: "UTC" });
                              const year = String(d.getUTCFullYear()).slice(-2);

                              return `${day} ${month} '${year}`;
                            })()
                            : ""}
                        </span>
                      </div>
                      {/* Feedback */}

                      {/* Feedback - always reserve space */}
                      <div className="flex items-center gap-2 w-[70px]" >
                        {term.feedback?.comments > 0 && (
                          <span className="flex items-center gap-1 text-[13px] text-gray-600">
                            <MessageSquare size={13} className="text-gray-500" />
                            {term.feedback.comments}
                          </span>
                        )}
                        {term.feedback?.issues > 0 && (
                          <span className="flex items-center gap-1 text-[13px] text-amber-500">
                            <AlertTriangle size={13} className="text-amber-500" />
                            {term.feedback.issues}
                          </span>
                        )}
                      </div>

                    </div>
                  </div>
                ))}
              </div>

            )}

          </div>
        </div>

        {/* Modals */}
        {selectedTerm && (
          <ModalPop
            term={selectedTerm}
            assetDetail={assetDetail}
            assetDetailLoading={assetDetailLoading}
            onClose={() => { setSelectedTerm(null); setAssetDetail(null); }}
            onFeedbackSubmit={onFeedbackSubmit}
          />
        )}

        {showAddModal && (
          <AddModal
            onClose={() => setShowAddModal(false)}
            onSubmit={handleAddTerm}
          />
        )}
      </div>
    </>
  );
};

export default GlossaryDiscovery;